import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAccount, useSignOut } from "../auth/Gate.js";
import { useIsAdmin } from "../admin/useIsAdmin.js";

/**
 * Who you are, and the way out.
 *
 * There was no way out. `useSignOut` has existed since accounts were added and
 * nothing ever called it — the hook was written, the button never was, and the
 * only way to leave a session was to clear the site's cookies. This is the
 * button.
 *
 * Nothing renders when there is no auth provider. That is the mode `docker
 * compose up` runs in, where there is no account to name and a menu offering to
 * sign out of nothing is worse than no menu at all.
 */
export const AccountMenu: React.FC = () => {
  const account = useAccount();
  const signOut = useSignOut();
  const admin = useIsAdmin();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  // Closing it: a click anywhere else, or Escape. Both, because a menu that
  // only one of them closes is a menu that feels stuck.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!account || !signOut) return null;

  // The local part, uppercased. A full address does not fit a 26px circle and a
  // generic silhouette says nothing about which account you are in — which is
  // the question this answers on a machine that has signed into two.
  const initial = account.email.trim().charAt(0).toUpperCase() || "?";

  return (
    <div className="relative" ref={root}>
      <button
        type="button"
        className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-bay-800 text-[11px] font-semibold text-ink-200 transition-colors hover:bg-bay-750"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${account.email}`}
        title={account.email}
        onClick={() => setOpen((value) => !value)}
      >
        {initial}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-[34px] z-30 w-[232px] rounded-[8px] border border-[var(--edge)] bg-bay-850 p-1 shadow-lg"
        >
          {/* The address, not a name. PropelAuth only has a name if the tenant
              asks for one and the person filled it in, so a name here would be
              blank for most accounts — and the address is the thing that tells
              two sessions apart anyway. */}
          <p className="truncate px-2.5 py-2 text-[12px] text-ink-300" title={account.email}>
            {account.name ? (
              <>
                <span className="block truncate font-medium text-ink-100">{account.name}</span>
                <span className="block truncate">{account.email}</span>
              </>
            ) : (
              account.email
            )}
          </p>
          {admin ? (
            <Link
              role="menuitem"
              to="/admin"
              className="block w-full rounded-[6px] px-2.5 py-2 text-left text-[13px] text-ink-200 transition-colors hover:bg-bay-800 hover:text-ink-100"
              onClick={() => setOpen(false)}
            >
              Admin
            </Link>
          ) : null}
          <div className="my-1 h-px bg-[var(--edge)]" />
          <button
            type="button"
            role="menuitem"
            className="w-full rounded-[6px] px-2.5 py-2 text-left text-[13px] text-ink-200 transition-colors hover:bg-bay-800 hover:text-ink-100"
            onClick={signOut}
          >
            {"Sign out"}
          </button>
        </div>
      ) : null}
    </div>
  );
};
